import { ProductBundleOption, ProductOption, ProductOptionData } from "@/product-options/option.base";
import type { Store } from "@/store";
import { STORE_ADAPTER, STORE_TRANSFORMERS } from "@/store.constants";

export type UnboundProductOptionData = Omit<ProductOptionData, "product">

export type ProductData = {
  store          : Store;
  name           : string;
  campaign?      : string;
  campaignType?  : string;
  id             : string;
  alias          : string;
  currency       : string;
  options        : Map<string, UnboundProductOptionData>;
  buyLinkBaseUrl?: string;
  platformId     : string;
}

export type UnboundProductData = Omit<ProductData, "store">

export const SET_OPTION = Symbol("setOption");

type MinMaxType = {
  min: number,
  max: number
}

type PriceType = MinMaxType & {
  monthly: MinMaxType
}

type DiscountType = MinMaxType & {
  percentage: MinMaxType
}

type DevicesType = MinMaxType & {
  values: number[]
}

type SubscriptionType = MinMaxType & {
  values: number[]
}

export abstract class Product {
  private store           : Store;
  private name            : string;
  private campaign?       : string;
  private campaignType?   : string;
  private id              : string;
  private alias           : string;
  private currency        : string;
  private options         : Map<string, ProductOption>;
  private platformId      : string;
  private devices         : DevicesType;
  private subscriptions   : SubscriptionType;
  private discount        : DiscountType;
  private price           : PriceType;
  private discountedPrice : PriceType;
  private buyLinkBaseUrl ?: string;

  constructor(product: ProductData) {
    this.store = product.store;
    this.name = product.name;
    this.campaign = product.campaign;
    this.campaignType = product.campaignType;
    this.id = product.id;
    this.alias = product.alias;
    this.platformId = product.platformId;
    this.currency = product.currency;
    this.devices = {
      min   : Number.MAX_SAFE_INTEGER,
      max   : Number.MIN_SAFE_INTEGER,
      values: []
    };
    this.subscriptions = {
      min   : Number.MAX_SAFE_INTEGER,
      max   : Number.MIN_SAFE_INTEGER,
      values: []
    };
    this.discount = {
      min       : Number.MAX_SAFE_INTEGER,
      max       : Number.MIN_SAFE_INTEGER,
      percentage: {
        min: Number.MAX_SAFE_INTEGER,
        max: Number.MIN_SAFE_INTEGER
      }
    };
    this.price = {
      min    : Number.MAX_SAFE_INTEGER,
      max    : Number.MIN_SAFE_INTEGER,
      monthly: {
        min: Number.MAX_SAFE_INTEGER,
        max: Number.MIN_SAFE_INTEGER
      }
    };
    this.discountedPrice = {
      min    : Number.MAX_SAFE_INTEGER,
      max    : Number.MIN_SAFE_INTEGER,
      monthly: {
        min: Number.MAX_SAFE_INTEGER,
        max: Number.MIN_SAFE_INTEGER
      }
    };
    this.options = new Map();
    this.buyLinkBaseUrl = product.buyLinkBaseUrl;

    const devices = new Set<number>();
    const subscriptions = new Set<number>();

    for (const [key, option] of product.options.entries()) {
      const productOption = this._createOption({ ...option, product: this })
      this.options.set(key, productOption)

      this.price.min = Math.min(
        productOption.getPrice({ currency: false }),
        this.price.min
      );
      this.price.max = Math.max(
        productOption.getPrice({ currency: false }),
        this.price.max
      );

      this.price.monthly.min = Math.min(
        productOption.getPrice({ monthly: true, currency: false }),
        this.price.monthly.min
      );
      this.price.monthly.max = Math.max(
        productOption.getPrice({ monthly: true, currency: false }),
        this.price.monthly.max
      );

      this.discountedPrice.min = Math.min(
        productOption.getDiscountedPrice({ currency: false }),
        this.discountedPrice.min
      );
      this.discountedPrice.max = Math.max(
        productOption.getDiscountedPrice({ currency: false }),
        this.discountedPrice.max
      );

      this.discountedPrice.monthly.min = Math.min(
        productOption.getDiscountedPrice({ monthly: true, currency: false }),
        this.discountedPrice.monthly.min
      );
      this.discountedPrice.monthly.max = Math.max(
        productOption.getDiscountedPrice({ monthly: true, currency: false }),
        this.discountedPrice.monthly.max
      );

      this.devices.min = Math.min(productOption.getDevices(), this.devices.min);
      this.devices.max = Math.max(productOption.getDevices(), this.devices.max);
      devices.add(productOption.getDevices());

      this.subscriptions.min = Math.min(productOption.getSubscription(), this.subscriptions.min);
      this.subscriptions.max = Math.max(productOption.getSubscription(), this.subscriptions.max);
      subscriptions.add(productOption.getSubscription());

      this.discount.min = Math.min(productOption.getDiscount({ symbol: false }), this.discount.min);
      this.discount.max = Math.max(productOption.getDiscount({ symbol: false }), this.discount.max);

      this.discount.percentage.min = Math.min(productOption.getDiscount({ percentage: true, symbol: false }), this.discount.percentage.min);
      this.discount.percentage.max = Math.max(productOption.getDiscount({ percentage: true, symbol: false }), this.discount.percentage.max);
    }

    this.devices.values = [...devices];
    this.subscriptions.values = [...subscriptions];
  }

  protected abstract _createOption(product: ProductOptionData): ProductOption;

  getStore() {
    return this.store;
  }

  getId() {
    return this.id;
  }

  getAlias() {
    return this.alias;
  }

  getPlatformId() {
    return this.platformId;
  }

  getName() {
    return this.name;
  }

  getCampaign() {
    return this.campaign;
  }

  getCampaignType() {
    return this.campaignType;
  }

  getCurrency() {
    return this.currency;
  }

  getBuyLinkBaseUrl() {
    return this.buyLinkBaseUrl;
  }

  async getOption(): Promise<ProductOption[]>
  async getOption(param: { devices: undefined, subscription: undefined, bundle: ProductBundleOption[] }): Promise<(ProductOption | undefined)[]>
  async getOption(param: { devices: number, subscription: number, bundle?: (ProductBundleOption | undefined)[] }): Promise<ProductOption | undefined>
  async getOption(param?: { devices?: number, subscription?: number, bundle?: (ProductBundleOption | undefined)[] }): Promise<ProductOption | (ProductOption | undefined)[] | undefined> {
    const adaptor = await this.store[STORE_ADAPTER];
    const { devices, subscription } = await adaptor.adaptTo({
      id          : this.alias,
      subscription: Number(param?.subscription),
      devices     : Number(param?.devices)
    });
    const bundle = param?.bundle?.filter((option): option is ProductBundleOption => Boolean(option));

    const makeBundle = async (option: ProductOption, bundle: ProductBundleOption[]) => {
      const bundledOption = await this.bundle(option, bundle);

      const transformer = this.store[STORE_TRANSFORMERS]?.buyLink;
      if (transformer) {
        const productData: UnboundProductData = {
          name          : this.name,
          campaign      : this.campaign,
          campaignType  : this.campaignType,
          id            : this.id,
          alias         : this.alias,
          currency      : this.currency,
          options       : new Map(),
          buyLinkBaseUrl: this.buyLinkBaseUrl,
          platformId    : this.platformId
        };

        bundledOption.buyLink = await transformer({
          buyLink: bundledOption.buyLink,
          product: productData,
          option : bundledOption
        });
      }

      return this._createOption({ ...bundledOption, product: this });
    }

    if (Number(devices) && Number(subscription)) {
      const option = this.options.get(`${devices}-${subscription}`);
      if (!option) return undefined;

      //bundle with the option
      if (bundle && bundle.length > 0) {
        return await makeBundle(option, bundle);
      } else {
        return option;
      }
    }

    if (bundle && bundle.length > 0) {
      const optionsWithBundle = [...this.options.values()].map(option => makeBundle(option, bundle));
      const settled = await Promise.allSettled(optionsWithBundle);
      return settled
        .map((result) => result.status === "fulfilled" ? result.value : undefined)
    }

    //return all options
    return [...this.options.values()];
  }

  [SET_OPTION](param: { options: ProductOption | ProductOption[] }) {
    const options  = Array.isArray(param.options) ? param.options : [param.options];
    for (const option of options) {
      const devices = option.getDevices();
      const subscription = option.getSubscription();

      this.options.set(`${devices}-${subscription}`, option)
    }
  }

  getPrice(): { min: string, max: string };
  getPrice(param: { monthly?: boolean, currency: true }): { min: string, max: string };
  getPrice(param: { monthly?: boolean, currency?: false }): { min: number, max: number };
  getPrice(param?: { monthly?: boolean, currency?: boolean }) {
    const { monthly = false, currency = true } = param ?? {};

    const price = monthly
      ? this.price.monthly
      : this.price;

    if (currency) {
      return {
        min: this.store.formatPrice({ price: price.min, currency: this.currency }),
        max: this.store.formatPrice({ price: price.max, currency: this.currency })
      };
    }

    return {
      min: price.min,
      max: price.max
    };
  }

  getDiscountedPrice(): { min: string, max: string };
  getDiscountedPrice(param: { monthly?: boolean, currency: true }): { min: string, max: string };
  getDiscountedPrice(param: {  monthly?: boolean, currency?: false }): { min: number, max: number };
  getDiscountedPrice(param?: { monthly?: boolean, currency?: boolean }) {
    const { monthly = false, currency = true } = param ?? {};

    const discountedPrice = monthly
      ? this.discountedPrice.monthly
      : this.discountedPrice;

    if (currency) {
      return {
        min: this.store.formatPrice({ price: discountedPrice.min, currency: this.currency }),
        max: this.store.formatPrice({ price: discountedPrice.max, currency: this.currency })
      };
    }

    return {
      min: discountedPrice.min,
      max: discountedPrice.max
    };
  }

  getDiscount(): { min: number, max: number };
  getDiscount(param: { percentage?: boolean; symbol: true }): { min: string, max: string };
  getDiscount(param: { percentage?: boolean; symbol?: false }): { min: number, max: number };
  getDiscount(param?: { percentage?: boolean, symbol?: boolean }) {
    const { percentage = false, symbol = true } = param ?? {};

    const discountValue = percentage
      ? this.discount.percentage
      : this.discount;

    if (percentage) {
      return symbol
        ? { min: `${discountValue.min}%`, max: `${discountValue.max}%` }
        : { min: discountValue.min, max: discountValue.max }
    }

    return symbol
      ? {
        min: this.store.formatPrice({ price: discountValue.min, currency: this.currency }),
        max: this.store.formatPrice({ price: discountValue.max, currency: this.currency })
      }
      : {
        min: discountValue.min,
        max: discountValue.max
      };
  }

  getDevices() {
    return this.devices;
  }

  getSubscriptions() {
    return this.subscriptions;
  }

  protected async bundle(base: ProductOption, options: ProductBundleOption[]): Promise<UnboundProductOptionData> {
    const round = (n: number): number => Number(n.toFixed(2));

    // Start with the first option as the base
    const bundledOption: UnboundProductOptionData = {
      price          : base.getPrice({ currency: false }),
      discountedPrice: base.getDiscountedPrice({ currency: false }),
      buyLink        : base.getBuyLink(),
      subscription   : base.getSubscription(),
      devices        : base.getDevices(),
      bundle         : options
    };

    // Process the remaining options
    for (const { option } of options) {
      const optionPrice = option.getPrice({ currency: false });
      const optionDiscountedPrice = option.getDiscountedPrice({ currency: false });

      // Always add the option's price to the bundled price
      if (bundledOption.discountedPrice && optionDiscountedPrice) {
        bundledOption.discountedPrice = round(bundledOption.discountedPrice + optionDiscountedPrice);
        bundledOption.price = round(bundledOption.price + optionPrice);
      }

      if (bundledOption.discountedPrice && !optionDiscountedPrice) {
        bundledOption.discountedPrice = round(bundledOption.discountedPrice + optionPrice);
        bundledOption.price = round(bundledOption.price + optionPrice);
      }

      if (!bundledOption.discountedPrice && optionDiscountedPrice) {
        bundledOption.discountedPrice = round(bundledOption.price + optionDiscountedPrice);
        bundledOption.price = round(bundledOption.price + optionPrice);
      }

      if (!bundledOption.discountedPrice && !optionDiscountedPrice) {
        bundledOption.price = round(bundledOption.price + optionPrice);
      }
    }

    return bundledOption;
  }
}
